import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Button
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import EditNoteIcon from "@mui/icons-material/EditNote";
import type { Feature, FeatureCollection, GeoJsonObject, Geometry } from "geojson";
import type { MapViewOptions, MapViewportSnapshot, UavMapElement } from "@impleotv-pm/uav-map-control";
import { useAppContext } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { resolveTileLayersJson, sanitizeTileLayersJson } from "../map/tileLayers";
import type { UserGroupSummary } from "../types/user";
import type { AnnotationStorage, AnnotationSummary, AnnotationUpsertInput } from "../types/annotation";

type AnnotationDialogProps = {
  open: boolean;
  title: string;
  submitLabel: string;
  isAdmin: boolean;
  annotation: AnnotationSummary | null;
  availableUserGroups: UserGroupSummary[];
  onClose: () => void;
  onSubmit: (input: AnnotationUpsertInput) => Promise<void>;
};

type AnnotationDialogTab = "general" | "map";

type AnnotationFormValues = {
  name: string;
  description: string;
  storage: AnnotationStorage;
  active: boolean;
  tagsText: string;
  userGroups: string[];
  restricted: boolean;
  featureCollection: FeatureCollection | null;
};

type AnnotationEventDetail = {
  id: string;
  geojson?: GeoJsonObject;
};

const FORM_ANNOTATION_ID = "annotation-form-current";
const ANNOTATION_MAP_VIEWPORT_STORAGE_KEY = "sky-view.annotations.mapViewport";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isFeatureCollectionGeoJson = (value: unknown): value is FeatureCollection =>
  isRecord(value) && value.type === "FeatureCollection" && Array.isArray(value.features);

const isFeatureGeoJson = (value: unknown): value is Feature =>
  isRecord(value) && value.type === "Feature" && isRecord(value.geometry);

const isGeometryGeoJson = (value: unknown): value is Geometry =>
  isRecord(value) && typeof value.type === "string" && value.type !== "Feature" && value.type !== "FeatureCollection";

const cloneGeoJson = <T extends GeoJsonObject>(geojson: T): T => JSON.parse(JSON.stringify(geojson)) as T;

const normalizeFeatureCollection = (value: unknown): FeatureCollection | null => {
  if (!isFeatureCollectionGeoJson(value)) {
    return null;
  }

  return cloneGeoJson(value);
};

const aggregateAnnotationGeoJson = (geojsons: Iterable<GeoJsonObject>): FeatureCollection | null => {
  const features: Feature[] = [];

  for (const geojson of geojsons) {
    if (isFeatureCollectionGeoJson(geojson)) {
      features.push(...cloneGeoJson(geojson).features);
      continue;
    }

    if (isFeatureGeoJson(geojson)) {
      features.push(cloneGeoJson(geojson));
      continue;
    }

    if (isGeometryGeoJson(geojson)) {
      features.push({
        type: "Feature",
        geometry: cloneGeoJson(geojson),
        properties: {}
      });
    }
  }

  return features.length > 0
    ? {
      type: "FeatureCollection",
      features
    }
    : null;
};

const stringifyGeoJson = (value: FeatureCollection | null): string =>
  value ? JSON.stringify(value) : "";

const normalizeTagList = (tagsText: string): string[] =>
  Array.from(
    new Set(
      tagsText
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
    )
  );

const normalizeUserGroupIds = (userGroups: string[]): string[] =>
  [...userGroups].sort((left, right) => left.localeCompare(right));

const buildComparableAnnotationState = (values: AnnotationFormValues) => {
  const featureCollection = normalizeFeatureCollection(values.featureCollection);

  return {
    name: values.name.trim(),
    description: values.description.trim(),
    storage: values.storage,
    active: values.active,
    tags: normalizeTagList(values.tagsText),
    userGroups: normalizeUserGroupIds(values.userGroups),
    restricted: values.restricted,
    featureCollectionSignature: stringifyGeoJson(featureCollection)
  };
};

const buildAnnotationInput = (values: AnnotationFormValues): AnnotationUpsertInput | null => {
  const featureCollection = normalizeFeatureCollection(values.featureCollection);
  if (!featureCollection || featureCollection.features.length === 0) {
    return null;
  }

  return {
    name: values.name.trim(),
    description: values.description.trim(),
    storage: values.storage,
    active: values.active,
    tags: normalizeTagList(values.tagsText),
    userGroups: normalizeUserGroupIds(values.userGroups),
    featureCollection,
    bbox: computeGeoJSONBBox(featureCollection),
    restricted: values.restricted
  };
};

const extractBBoxFromCoordinates = (
  coordinates: unknown,
  acc: [number, number, number, number] | null
): [number, number, number, number] | null => {
  if (!Array.isArray(coordinates)) {
    return acc;
  }

  if (
    coordinates.length >= 2 &&
    typeof coordinates[0] === "number" &&
    Number.isFinite(coordinates[0]) &&
    typeof coordinates[1] === "number" &&
    Number.isFinite(coordinates[1])
  ) {
    const lon = coordinates[0];
    const lat = coordinates[1];
    if (!acc) {
      return [lon, lat, lon, lat];
    }

    return [
      Math.min(acc[0], lon),
      Math.min(acc[1], lat),
      Math.max(acc[2], lon),
      Math.max(acc[3], lat)
    ];
  }

  let next = acc;
  for (const entry of coordinates) {
    next = extractBBoxFromCoordinates(entry, next);
  }
  return next;
};

const computeGeoJSONBBox = (featureCollection: FeatureCollection | null): number[] => {
  if (!featureCollection) {
    return [];
  }

  if (
    Array.isArray(featureCollection.bbox) &&
    featureCollection.bbox.length === 4 &&
    featureCollection.bbox.every((entry) => typeof entry === "number" && Number.isFinite(entry))
  ) {
    return [...featureCollection.bbox];
  }

  let bbox: [number, number, number, number] | null = null;
  for (const feature of featureCollection.features) {
    const geometry = feature.geometry;
    bbox = extractBBoxFromCoordinates(
      geometry && "coordinates" in geometry ? geometry.coordinates : null,
      bbox
    );
  }

  return bbox ? [...bbox] : [];
};

const normalizeAnnotationMapViewport = (viewport: MapViewportSnapshot | null | undefined): MapViewOptions | null => {
  const centerLat = Number(viewport?.center?.lat);
  const centerLon = Number(viewport?.center?.lng);
  const zoom = Number(viewport?.zoom);
  const west = Number(viewport?.bounds?.getWest?.());
  const south = Number(viewport?.bounds?.getSouth?.());
  const east = Number(viewport?.bounds?.getEast?.());
  const north = Number(viewport?.bounds?.getNorth?.());

  const hasCenter = Number.isFinite(centerLat) && Number.isFinite(centerLon);
  const hasZoom = Number.isFinite(zoom);
  const hasBounds =
    Number.isFinite(west) &&
    Number.isFinite(south) &&
    Number.isFinite(east) &&
    Number.isFinite(north);

  if (!hasCenter && !hasBounds) {
    return null;
  }

  return {
    center: hasCenter ? [centerLat, centerLon] : undefined,
    zoom: hasZoom ? zoom : undefined,
    bounds: hasBounds
      ? [
        [south, west],
        [north, east]
      ]
      : undefined
  };
};

const isValidStoredAnnotationMapViewport = (value: unknown): value is MapViewOptions => {
  if (!isRecord(value)) {
    return false;
  }

  const center = value.center;
  const zoom = value.zoom;
  const bounds = value.bounds;
  const hasCenter =
    Array.isArray(center) &&
    center.length === 2 &&
    center.every((entry) => typeof entry === "number" && Number.isFinite(entry));
  const hasZoom = typeof zoom === "number" && Number.isFinite(zoom);
  const hasBounds =
    Array.isArray(bounds) &&
    bounds.length === 2 &&
    bounds.every(
      (point) =>
        Array.isArray(point) &&
        point.length === 2 &&
        point.every((entry) => typeof entry === "number" && Number.isFinite(entry))
    );

  return (hasCenter || hasBounds) && (zoom === undefined || hasZoom);
};

const readStoredAnnotationMapViewport = (): MapViewOptions | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(ANNOTATION_MAP_VIEWPORT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!isValidStoredAnnotationMapViewport(parsed)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const persistAnnotationMapViewport = (mapElement: UavMapElement | null) => {
  if (typeof window === "undefined" || !mapElement) {
    return;
  }

  try {
    const viewport = normalizeAnnotationMapViewport(mapElement.GetViewport?.());
    if (!viewport) {
      return;
    }

    window.localStorage.setItem(
      ANNOTATION_MAP_VIEWPORT_STORAGE_KEY,
      JSON.stringify(viewport)
    );
  } catch {
    // ignore storage failures
  }
};

const restoreStoredAnnotationMapViewport = (mapElement: UavMapElement | null): boolean => {
  const storedViewport = readStoredAnnotationMapViewport();
  if (!mapElement || !storedViewport) {
    return false;
  }

  mapElement.SetViewport?.(storedViewport);

  return true;
};

const validationSchema = Yup.object({
  name: Yup.string()
    .transform((value) => value?.trim() ?? "")
    .required("Name is required"),
  description: Yup.string().transform((value) => value?.trim() ?? ""),
  storage: Yup.string().oneOf(["server", "local"]).required("Storage is required"),
  tagsText: Yup.string().transform((value) => value ?? ""),
  userGroups: Yup.array().of(Yup.string().transform((value) => value?.trim() ?? "")),
  restricted: Yup.boolean().required(),
  active: Yup.boolean().required(),
  featureCollection: Yup.mixed<FeatureCollection>()
    .nullable()
    .test(
      "feature-collection-required",
      "Feature collection is required.",
      (value) => isFeatureCollectionGeoJson(value) && value.features.length > 0
    )
});

type AnnotationMapFieldProps = {
  open: boolean;
  editable: boolean;
  featureCollection: FeatureCollection | null;
  onFeatureCollectionChange: (featureCollection: FeatureCollection | null) => void;
};

const AnnotationMapField = ({
  open,
  editable,
  featureCollection,
  onFeatureCollectionChange
}: AnnotationMapFieldProps) => {
  const { resolveApiPath, tokens } = useAuth();
  const { serverConfig } = useAppContext();
  const tileLayers = useMemo(
    () => sanitizeTileLayersJson(
      resolveTileLayersJson(serverConfig.mapTileLayers),
      resolveApiPath("/"),
      tokens?.accessToken
    ) ?? resolveTileLayersJson(null),
    [resolveApiPath, serverConfig.mapTileLayers, tokens?.accessToken]
  );
  const featureCollectionSignature = useMemo(() => stringifyGeoJson(featureCollection), [featureCollection]);

  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapElementRef = useRef<UavMapElement | null>(null);
  const annotationGeoJsonRef = useRef<Map<string, GeoJsonObject>>(new Map());
  const callbackRef = useRef(onFeatureCollectionChange);
  const featureCollectionRef = useRef(featureCollection);
  const featureCollectionSignatureRef = useRef(featureCollectionSignature);
  const pendingInternalSignatureRef = useRef<string | null>(null);
  const lastAppliedSignatureRef = useRef("");
  const lastFocusedSignatureRef = useRef<string | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  useEffect(() => {
    callbackRef.current = onFeatureCollectionChange;
  }, [onFeatureCollectionChange]);

  useEffect(() => {
    featureCollectionRef.current = featureCollection;
    featureCollectionSignatureRef.current = featureCollectionSignature;
  }, [featureCollection, featureCollectionSignature]);

  const syncMapAnnotations = useCallback((nextFeatureCollection: FeatureCollection | null) => {
    const mapElement = mapElementRef.current;
    if (!mapElement) {
      return;
    }

    for (const annotationId of Array.from(annotationGeoJsonRef.current.keys())) {
      mapElement.RemoveAnnotation?.(annotationId);
    }
    annotationGeoJsonRef.current.clear();

    if (nextFeatureCollection && nextFeatureCollection.features.length > 0) {
      const cloned = cloneGeoJson(nextFeatureCollection);
      annotationGeoJsonRef.current.set(FORM_ANNOTATION_ID, cloned);
      mapElement.AddAnnotation?.(FORM_ANNOTATION_ID, cloned);
    }
  }, []);

  const focusMapOnFeatureCollection = useCallback((nextFeatureCollection: FeatureCollection | null, signature?: string) => {
    const mapElement = mapElementRef.current;
    if (!mapElement || !nextFeatureCollection) {
      return;
    }

    const nextSignature = signature ?? stringifyGeoJson(nextFeatureCollection);
    if (!nextSignature || nextSignature === lastFocusedSignatureRef.current) {
      return;
    }

    const bbox = computeGeoJSONBBox(nextFeatureCollection);
    if (bbox.length !== 4) {
      return;
    }

    const [minLon, minLat, maxLon, maxLat] = bbox;
    if (minLon === maxLon && minLat === maxLat) {
      mapElement.SetViewport?.({ center: [minLat, minLon] });
      lastFocusedSignatureRef.current = nextSignature;
      return;
    }

    mapElement.SetViewport?.({
      bounds: [
        [minLat, minLon],
        [maxLat, maxLon]
      ]
    });
    lastFocusedSignatureRef.current = nextSignature;
  }, []);

  useEffect(() => {
    if (!open || !hostRef.current) {
      return;
    }

    let disposed = false;
    let detachListeners: (() => void) | null = null;

    void (async () => {
      try {
        await import("@impleotv-pm/uav-map-control");
        if (disposed || !hostRef.current) {
          return;
        }

        const mapElement = document.createElement("uav-map") as UavMapElement;
        mapElement.style.display = "block";
        mapElement.style.width = "100%";
        mapElement.style.height = "100%";

        const updateFeatureCollection = () => {
          const aggregated = aggregateAnnotationGeoJson(annotationGeoJsonRef.current.values());
          const nextSignature = stringifyGeoJson(aggregated);
          pendingInternalSignatureRef.current = nextSignature;
          callbackRef.current(aggregated);
        };

        const handleCreated = (event: Event) => {
          const detail = (event as CustomEvent<AnnotationEventDetail>).detail;
          if (!detail?.id || !detail.geojson) {
            return;
          }
          annotationGeoJsonRef.current.set(detail.id, cloneGeoJson(detail.geojson));
          updateFeatureCollection();
        };

        const handleUpdated = (event: Event) => {
          const detail = (event as CustomEvent<AnnotationEventDetail>).detail;
          if (!detail?.id || !detail.geojson) {
            return;
          }
          annotationGeoJsonRef.current.set(detail.id, cloneGeoJson(detail.geojson));
          updateFeatureCollection();
        };

        const handleDeleted = (event: Event) => {
          const detail = (event as CustomEvent<AnnotationEventDetail>).detail;
          if (!detail?.id) {
            return;
          }
          annotationGeoJsonRef.current.delete(detail.id);
          updateFeatureCollection();
        };

        mapElement.addEventListener("uavmap:annotation-created", handleCreated);
        mapElement.addEventListener("uavmap:annotation-updated", handleUpdated);
        mapElement.addEventListener("uavmap:annotation-deleted", handleDeleted);

        hostRef.current.replaceChildren(mapElement);
        mapElementRef.current = mapElement;
        mapElement.SetTileLayers?.(tileLayers);
        mapElement.SetAutoCenterVisible?.(false);
        mapElement.SetMarkerVisible?.(false);
        mapElement.SetRulerVisible?.(false);
        mapElement.SetAreaCoverageSearchVisible?.(false);
        mapElement.EditAnnotations?.(editable);
        syncMapAnnotations(featureCollectionRef.current);
        if (featureCollectionRef.current?.features.length) {
          focusMapOnFeatureCollection(featureCollectionRef.current, featureCollectionSignatureRef.current);
        } else {
          restoreStoredAnnotationMapViewport(mapElement);
        }
        lastAppliedSignatureRef.current = featureCollectionSignatureRef.current;
        setMapError(null);

        detachListeners = () => {
          persistAnnotationMapViewport(mapElement);
          mapElement.removeEventListener("uavmap:annotation-created", handleCreated);
          mapElement.removeEventListener("uavmap:annotation-updated", handleUpdated);
          mapElement.removeEventListener("uavmap:annotation-deleted", handleDeleted);
          mapElement.remove();
          mapElementRef.current = null;
          annotationGeoJsonRef.current.clear();
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unable to load map editor.";
        setMapError(message);
      }
    })();

    return () => {
      disposed = true;
      detachListeners?.();
      hostRef.current?.replaceChildren();
    };
  }, [editable, focusMapOnFeatureCollection, open, syncMapAnnotations, tileLayers]);

  useEffect(() => {
    if (!mapElementRef.current) {
      return;
    }

    mapElementRef.current.EditAnnotations?.(editable);
  }, [editable]);

  useEffect(() => {
    if (!open || !mapElementRef.current) {
      return;
    }

    if (pendingInternalSignatureRef.current !== null && featureCollectionSignature === pendingInternalSignatureRef.current) {
      lastAppliedSignatureRef.current = featureCollectionSignature;
      pendingInternalSignatureRef.current = null;
      return;
    }

    if (featureCollectionSignature === lastAppliedSignatureRef.current) {
      return;
    }

    syncMapAnnotations(featureCollection);
    focusMapOnFeatureCollection(featureCollection, featureCollectionSignature);
    lastAppliedSignatureRef.current = featureCollectionSignature;
  }, [featureCollection, featureCollectionSignature, focusMapOnFeatureCollection, open, syncMapAnnotations]);

  return (
    <Stack spacing={2}>
      {mapError ? <Alert severity="error">{mapError}</Alert> : null}
      <Box
        ref={hostRef}
        sx={{
          height: 420,
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "background.default",
          "& > uav-map": {
            display: "block",
            width: "100%",
            height: "100%"
          }
        }}
      />
    </Stack>
  );
};

const AnnotationDialog = ({
  open,
  title,
  submitLabel,
  isAdmin,
  annotation,
  availableUserGroups,
  onClose,
  onSubmit
}: AnnotationDialogProps) => {
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AnnotationDialogTab>("general");

  useEffect(() => {
    if (!open) {
      return;
    }

    setDialogError(null);
    setActiveTab("general");
  }, [annotation, open]);

  const userGroupMap = useMemo(() => {
    const entries = availableUserGroups.map((group) => [group.id, group.name] as const);
    return new Map(entries);
  }, [availableUserGroups]);

  const initialValues = useMemo<AnnotationFormValues>(
    () => ({
      name: annotation?.name ?? "",
      description: annotation?.description ?? "",
      storage: annotation?.storage ?? (isAdmin ? "server" : "local"),
      active: annotation?.active ?? true,
      tagsText: (annotation?.tags ?? []).join(", "),
      userGroups: annotation?.userGroups ?? [],
      restricted: annotation?.restricted ?? false,
      featureCollection: normalizeFeatureCollection(annotation?.featureCollection ?? null)
    }),
    [annotation, isAdmin]
  );

  const handleDialogClose = (_event?: unknown, reason?: string) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box display="inline-flex" alignItems="center" gap={1}>
          <EditNoteIcon sx={{ color: "text.secondary" }} />
          {title}
        </Box>
      </DialogTitle>
      <Formik<AnnotationFormValues>
        initialValues={initialValues}
        enableReinitialize
        validateOnMount
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setDialogError(null);

          const nextInput = buildAnnotationInput(values);
          if (!nextInput) {
            setDialogError("Feature collection is required.");
            setSubmitting(false);
            return;
          }

          try {
            await onSubmit(nextInput);
            onClose();
          } catch (err) {
            const message = err instanceof Error ? err.message : "Unable to save annotation.";
            setDialogError(message);
            setSubmitting(false);
          }
        }}
      >
        {(formik) => {
          const nameError = (formik.touched.name || formik.submitCount > 0) && formik.errors.name;
          const featureCollectionError =
            (formik.touched.featureCollection || formik.submitCount > 0) && formik.errors.featureCollection;
          const initialComparableState = buildComparableAnnotationState(initialValues);
          const currentComparableState = buildComparableAnnotationState(formik.values);
          const hasChanges = !annotation || JSON.stringify(currentComparableState) !== JSON.stringify(initialComparableState);
          const isReadOnly = !isAdmin && initialValues.restricted;

          return (
            <Form>
              <DialogContent sx={{ pt: 1.5 }}>
                <Stack spacing={2}>
                  {dialogError ? <Alert severity="error">{dialogError}</Alert> : null}
                  {!isAdmin ? (
                    <Alert severity={isReadOnly ? "warning" : "info"}>
                      {isReadOnly
                        ? "Restricted annotations can only be edited or deleted by administrators."
                        : "Only administrators can change the Restricted flag. Unrestricted annotations can be saved to local or server storage."}
                    </Alert>
                  ) : null}
                  <Tabs
                    value={activeTab}
                    onChange={(_event, value: AnnotationDialogTab) => setActiveTab(value)}
                    aria-label="Annotation dialog tabs"
                    sx={{ minHeight: 40 }}
                  >
                    <Tab value="general" label="General" sx={{ minHeight: 40, py: 0.5 }} />
                    <Tab value="map" label="Map" sx={{ minHeight: 40, py: 0.5 }} />
                  </Tabs>

                  {activeTab === "general" ? (
                    <Stack spacing={2}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                        <TextField
                          label="Name"
                          name="name"
                          fullWidth
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={isReadOnly}
                          error={Boolean(nameError)}
                          helperText={nameError || " "}
                          autoFocus
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formik.values.active}
                              onChange={(_event, checked) => {
                                void formik.setFieldValue("active", checked);
                              }}
                              color="primary"
                              disabled={isReadOnly}
                            />
                          }
                          label={formik.values.active ? "Active" : "Disabled"}
                          sx={{ m: 0, flexShrink: 0 }}
                        />
                      </Stack>
                      <TextField
                        label="Description"
                        name="description"
                        fullWidth
                        multiline
                        minRows={3}
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={isReadOnly}
                      />
                      <TextField
                        label="Tags"
                        name="tagsText"
                        fullWidth
                        value={formik.values.tagsText}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={isReadOnly}
                      />
                      <FormControl fullWidth>
                        <InputLabel id="annotation-usergroups-label">User groups</InputLabel>
                        <Select
                          labelId="annotation-usergroups-label"
                          multiple
                          value={formik.values.userGroups}
                          label="User groups"
                          onChange={(event) => {
                            const value = event.target.value;
                            void formik.setFieldValue(
                              "userGroups",
                              typeof value === "string" ? value.split(",") : value
                            );
                          }}
                          onBlur={() => {
                            void formik.setFieldTouched("userGroups", true);
                          }}
                          disabled={isReadOnly}
                          renderValue={(selected) =>
                            (selected as string[])
                              .map((groupId) => userGroupMap.get(groupId) ?? groupId)
                              .join(", ") || "None"
                          }
                        >
                          {availableUserGroups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                              <Checkbox checked={formik.values.userGroups.includes(group.id)} />
                              <ListItemText primary={group.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                        <FormControl sx={{ minWidth: 140, width: { xs: "100%", sm: "fit-content" }, flexShrink: 0 }}>
                          <InputLabel id="annotation-storage-label">Storage</InputLabel>
                          <Select
                            labelId="annotation-storage-label"
                            value={formik.values.storage}
                            label="Storage"
                            onChange={(event) => {
                              void formik.setFieldValue("storage", event.target.value as AnnotationStorage);
                            }}
                            disabled={isReadOnly}
                          >
                            <MenuItem value="local">Local</MenuItem>
                            <MenuItem value="server">Server</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formik.values.restricted}
                              onChange={(_event, checked) => {
                                void formik.setFieldValue("restricted", checked);
                              }}
                              color="primary"
                              disabled={!isAdmin}
                            />
                          }
                          label="Restricted"
                          sx={{ m: 0 }}
                        />
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      {featureCollectionError ? <Alert severity="error">{featureCollectionError}</Alert> : null}
                      <AnnotationMapField
                        open={open && activeTab === "map"}
                        editable={!isReadOnly}
                        featureCollection={formik.values.featureCollection}
                        onFeatureCollectionChange={(nextFeatureCollection) => {
                          void formik.setFieldTouched("featureCollection", true, false);
                          void formik.setFieldValue("featureCollection", nextFeatureCollection, true);
                        }}
                      />
                    </Stack>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} disabled={formik.isSubmitting}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isReadOnly || formik.isSubmitting || !formik.isValid || !hasChanges}
                >
                  {submitLabel}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default AnnotationDialog;
